// Boots the dashboard: the mock's markup and styles, fed by the bridge.
import './no-eval.ts';
import Chart from 'chart.js/auto';
import { api, Unauthenticated } from './api.ts';
import { addSignOut, showUnreachable, signIn } from './login.ts';
import shell from './mock/shell.html?raw';
import {
  ceoPane,
  chooseDataSet,
  crmPane,
  currentDataSet,
  funnelPane,
  markUnavailable,
  NOT_BUILT,
  setDataSetSwitch,
  setFilterOptions,
  setSideNote,
  setStatusPills,
} from './panes.ts';
import './mock/mock.css';
import './unavailable.css';
import { explainOnHover } from './why.ts';

const REFRESH_MS = 5 * 60 * 1000; // re-read gold's "data as of" while the page stays open

// The mock's page behaviour (mock/behaviour.js, copied unchanged) expects a global Chart
// and a SECTIONS map whose init() runs the first time a pane is shown.
const page = window as unknown as {
  Chart: typeof Chart;
  SECTIONS: Record<string, { init(): void }>;
};
page.Chart = Chart;
const ceo = ceoPane(setStatusPills);
const funnel = funnelPane();
const crm = crmPane();
page.SECTIONS = {
  ceo,
  funnel,
  crm,
  ...Object.fromEntries(
    ['marketing', 'affiliates', 'risk'].map((id) => [id, { init: () => markUnavailable(id, NOT_BUILT) }]),
  ),
};

document.body.innerHTML = shell;
explainOnHover(); // reasons and definitions in a small pop-up, not the browser's slow title tooltip

const utcDay = () => new Date().toISOString().slice(0, 10);
let cardsDay = utcDay(); // the UTC day the date presets (Today, Last 7 days, ...) were worked out on

let capsGen = 0;
async function refreshCapabilities() {
  const mine = ++capsGen;
  const set = currentDataSet();
  try {
    const caps = await api.capabilities(set);
    if (mine !== capsGen || set !== currentDataSet()) {
      return; // a newer read or a data-set switch came after this one
    }
    setStatusPills(caps);
    setSideNote(caps.dataSource, caps.environment);
    const picksChanged = setFilterOptions(caps.filters);
    if (picksChanged.funnel) {
      funnel.reload(); // a new generation: an answer for the dropped pick is not shown
    }
    if (picksChanged.crm) {
      crm.reload();
    }
    setDataSetSwitch(caps.dataSets, (id) => {
      chooseDataSet(id);
      setFilterOptions({ country: [], source: [], game: [], currency: [] }); // the old set's picks don't apply
      ceo.reload(); // a new generation: the old set's late answer is dropped
      funnel.reload();
      crm.reload();
      void refreshCapabilities();
    });
    // Load the cards again when gold published since they loaded (so values and "data as of" agree),
    // when a UTC day ended ("Today" is now another day), or when the last load failed. ponytail:
    // noticed on the next refresh, up to REFRESH_MS later; a midnight timer if that ever matters.
    const shown = ceo.shownSnapshotId();
    if (
      picksChanged.ceo ||
      ceo.loadFailed() ||
      (shown !== undefined && (shown !== caps.snapshotId || utcDay() !== cardsDay))
    ) {
      cardsDay = utcDay();
      ceo.reload();
    }
  } catch (err) {
    if (mine !== capsGen) {
      return;
    }
    if (err instanceof Unauthenticated) {
      location.reload();
      return;
    }
    setStatusPills(undefined); // "Data time unknown", in red
  }
}

async function boot() {
  let user = await api.me().catch((err) => {
    if (err instanceof Unauthenticated) {
      return undefined;
    }
    throw err;
  });
  user ??= await signIn();
  addSignOut(user);
  // Draw the page first; the header's "data as of" fills in when gold answers.
  await import('./mock/behaviour.js'); // routing, mobile drawer, chart sizing; shows the first pane
  void refreshCapabilities();
  // A hidden tab doesn't refresh: every read keeps the Databricks warehouse awake (it stops after 10
  // idle minutes and bills while it runs), so a forgotten tab kept it up all day. Back in view, the
  // page refreshes at once if a refresh came due meanwhile. One timer, set again by every refresh,
  // so two refreshes are never closer than REFRESH_MS.
  let due = false;
  let timer: ReturnType<typeof setTimeout>;
  const refresh = () => {
    due = false;
    clearTimeout(timer);
    timer = setTimeout(onTimer, REFRESH_MS);
    void refreshCapabilities();
    // The funnel reads its own publication (DS's is not the summary's), so it loads again on its own,
    // quietly, whether or not the capabilities read worked; the CRM pane too (it retries what failed).
    funnel.reload(true);
    crm.reload(true);
  };
  const onTimer = () => {
    if (document.hidden) {
      due = true;
    } else {
      refresh();
    }
  };
  timer = setTimeout(onTimer, REFRESH_MS);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && due) {
      refresh();
    }
  });
}

boot().catch((err: unknown) => {
  showUnreachable((err as Error)?.message || 'The dashboard service cannot be reached.');
});
